import { selectIsInternalAdmin } from 'accessors'
import InsightsTooltip from 'components/ReusableComponents/Charts/Inslghts/InsightsTooltip'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AddGuestIcon from 'components/ReusableComponents/Icons/AddGuest'
import ProfileCompletedIcon from 'components/ReusableComponents/Icons/ProfileCompleted'
import ResearchCompleteIcon from 'components/ReusableComponents/Icons/ResearchComplete'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import { usePerformanceReportsDates } from 'features/Analytics'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import AnalyticsMaintenanceBar from 'features/Analytics/MaintenanceBar'
import { provideEndDate } from 'features/Analytics/provideEndDate'
import Diagnose from 'features/Diagnose'
import { barColors } from 'features/Engagement/engagementConstants'
import FactNumbers from 'features/Engagement/FactNumbers'
import useAllHotelsToggle from 'hooks/useAllHotels'
import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import subscriptionStatsApi from 'services/api/subscriptionStats'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './SubscriptionAnalyticsSnowflake.module.scss'

const { thisWeek, last7days, last14days, last30days } = smartDateTypes

const ChartBox = ({
  isLoading,
  error,
  data = [],
  title,
  dataTitle,
  color = barColors[0]
}) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      {isLoading && <LoadingIndicator inline />}
      {!isLoading && error && (
        <div className={styles.errorText}>
          Something went wrong while getting the data
        </div>
      )}
      {!isLoading && !error && data.length === 0 && (
        <div className={styles.errorText}>There is no data for selected date range</div>
      )}
      {!isLoading && !error && data && (
        <div className={styles.graphHolder}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={300}
              data={data}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 15
              }}
            >
              <CartesianGrid stroke="#F2F2F2" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                tick={{
                  fill: '#7D7D7D',
                  fontSize: 12,
                  transform: 'translate(0, 14)'
                }}
                tickSize={0}
                padding={{ top: 30, bottom: 20, right: 40 }}
              />
              <YAxis
                tickSize={0}
                axisLine={false}
                allowDecimals={false}
                yAxisId="left"
                tick={{
                  fill: '#7D7D7D',
                  fontSize: 12,
                  transform: 'translate(-10, 0)'
                }}
                padding={{ top: 40, bottom: 0 }}
              />
              <Tooltip
                content={<InsightsTooltip dataTitle={dataTitle} />}
                cursor={{
                  strokeWidth: 0,
                  fill: '#dedede',
                  opacity: 0.22
                }}
              />
              <Line
                dataKey="count"
                stroke={color}
                type="monotone"
                dot={false}
                strokeWidth={2}
                yAxisId="left"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

const SubscriptionAnalytics = ({ showTabs }) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const { addToast } = useToasts()

  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    parentChain,
    setParentChain,
    idsOfCurrentGroup,
    idsOfAllHotels
  } = useAllHotelsToggle()

  const [subscriptionsError, setSubscriptionsError] = useState()
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true)
  const [subscriptionStats, setSubscriptionStats] = useState()

  const pms = useSelector(state => state.hotelDetails.pms)

  const hasDisconnectedPMS = pms === PMSs.disconnected

  useEffect(() => {
    setIsSubscriptionsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .subscriptionsSnowflake(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: provideEndDate(toDate),
          kind
        },
        abortController
      )
      .then(res => {
        setSubscriptionsError()
        setSubscriptionStats(res)
        setIsSubscriptionsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('AbortError: signal is aborted without reason')) {
          setSubscriptionsError(err.toString())
          setIsSubscriptionsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain,
    addToast
  ])

  const isFactsLoading = isSubscriptionsLoading

  return (
    <div className={styles.wrapper}>
      {showTabs ? (
        <div className={styles.tabs}>
          <AnalyticTabs
            showWholeGroup={showWholeGroup}
            setShowWholeGroup={setShowWholeGroup}
            setShowAllHotels={setShowAllHotels}
            showAllHotels={showAllHotels}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fromDate={fromDate}
            toDate={toDate}
            parentChain={parentChain}
            setParentChain={setParentChain}
          />
          <AnalyticsMaintenanceBar />
          <DataIsReprocessingBar />
          <Diagnose />
        </div>
      ) : (
        <>
          {!showTabs && <CustomHeader title="Subscriptions" width={1180} />}
          <div className={styles.datepickerHolder}>
            <SmartDatepicker
              isRange={true}
              startDate={fromDate}
              endDate={toDate}
              minDate={moment().add(-4, 'week')}
              onChangeStartDate={setFromDate}
              onChangeEndDate={setToDate}
              smartDates={[thisWeek, last7days, last14days, last30days]}
              isLimited={true}
            />
          </div>
        </>
      )}

      <div className={styles.row}>
        <FactNumbers>
          {isFactsLoading && (
            <div style={{ width: '100%' }}>
              <LoadingIndicator inline />
            </div>
          )}
          {!isFactsLoading && subscriptionStats && (
            <>
              <FactNumbers.Item
                value={formatNumberAsThousands(subscriptionStats.guestAddedTotal)}
                delta={subscriptionStats.guestAdded}
                Icon={AddGuestIcon}
                width={55}
                height={55}
                label="Guests subscribed"
              />
              {hasDisconnectedPMS && (
                <FactNumbers.Item
                  value={formatNumberAsThousands(subscriptionStats.guestUpdatedTotal)}
                  delta={subscriptionStats.guestUpdated}
                  Icon={ProfileCompletedIcon}
                  label="Profiles completed"
                  width={55}
                  height={55}
                />
              )}
              <FactNumbers.Item
                value={formatNumberAsThousands(subscriptionStats.guestCompletedTotal)}
                delta={subscriptionStats.guestCompleted}
                Icon={ResearchCompleteIcon}
                width={55}
                height={55}
                label="Guest data enriched by AI"
              />
            </>
          )}
        </FactNumbers>
      </div>

      {isInternalAdmin && (
        <div className={styles.row}>
          <FactNumbers>
            {isFactsLoading && (
              <div style={{ width: '100%' }}>
                <LoadingIndicator inline />
              </div>
            )}
            {!isFactsLoading && subscriptionStats && (
              <>
                <FactNumbers.Item
                  value={formatNumberAsThousands(subscriptionStats.emailGuestAddedTotal)}
                  delta={subscriptionStats.emailGuestAdded}
                  Icon={AddGuestIcon}
                  width={55}
                  height={55}
                  label="Email guests subscribed"
                />

                <FactNumbers.Item
                  value={formatNumberAsThousands(
                    subscriptionStats.emailGuestUpdatedTotal
                  )}
                  delta={subscriptionStats.emailGuestUpdated}
                  Icon={ProfileCompletedIcon}
                  label="Email profiles updated"
                  width={55}
                  height={55}
                />

                <FactNumbers.Item
                  value={formatNumberAsThousands(
                    subscriptionStats.emailGuestCompletedTotal
                  )}
                  delta={subscriptionStats.emailGuestCompleted}
                  Icon={ResearchCompleteIcon}
                  width={55}
                  height={55}
                  label="Email guest data enriched by AI"
                />
              </>
            )}
          </FactNumbers>
        </div>
      )}
      <div className={styles.cards}>
        {hasDisconnectedPMS && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Front-desk performance</h3>
            </div>
          </div>
        )}
        <ChartBox
          title="Guests subscribed"
          isLoading={isSubscriptionsLoading}
          error={subscriptionsError}
          data={subscriptionStats?.guestAddedActivities}
          dataTitle="subscriptions"
          color={barColors[0]}
        />
        <ChartBox
          title="Guest data enriched by AI"
          isLoading={isSubscriptionsLoading}
          error={subscriptionsError}
          data={subscriptionStats?.guestCompletedActivities}
          dataTitle="enhanced profiles"
          color={barColors[0]}
        />
      </div>
    </div>
  )
}

export default SubscriptionAnalytics
